const env = process.env;

export const AppName = env.REACT_APP__ADMIN_APP_NAME;

//--------------ADMIN: Backend-Frontend common fields start-------------------------------
export const AdminBaseUrl = '/admin';

/**
 * The AdminModuleCtgs object serves as a centralized enumeration for categorizing
 * different sections of the Admin module within the application.
 *
 * Properties:
 * - NoCtg: Represents an uncategorized module.
 * - MasterMgmt: Denotes the Master Management section.
 * - ActivityMgmt: Denotes the Activity Management section.
 * - ResourceMgmt: Denotes the Resource Management section.
 * - DatabaseMgmt: Denotes the Database Management section.
 *
 * Note:
 * The order of these categories is used in the 'AdminSidebar.jsx' file. Modifying this order
 * may affect the functionality and appearance of the sidebar component.
 */
export const AdminModuleCtgs = {
    //This is order is being used in 'AdminSidebar.jsx' file, don't change
    NoCtg: 'No Category',
    MasterMgmt: 'Master Management',
    ActivityMgmt: 'Activity Management',
    ResourceMgmt: 'Resource Management',
    // UserMgmt: 'User Management',
    DatabaseMgmt: 'Database Management',
};

/**
 * AdminModules object holds the mapping of admin module keys to their corresponding display names.
 <p>
 * It centralizes the labels used across the admin interface.
 */
export const AdminModules = {
    //Auth
    Login: "Sign In",
    ForgotPw: "Forgot Password",

    //Main Pages
    Dashboard: "Dashboard",
    MyAccount: "My Account",

    //Main Modules
    ParentCompany: "Parent Company",
    Company: "Company",
    Members: "Members",
    Groups: "Groups",
    Country: "Country",
    State: "State",
    City: "City",
    ModuleAllocation: "Module Allocation",
    LoginBypassManager: "Login Bypass Manager",

    //User Modules
    Users: "Users",
    UserRoles: "User Roles",
    Reports: "Reports",

    //Activity
    Wm: "Web Meeting",
    Meeting: "Meeting",
    Forum: "Forum",
    Survey: "Survey",

    //Resource
    Pub: "Publication",
    Articles: "Articles",
    Doc: "Documents",
    CS: "Case Study",
    Videos: "Videos",
    Images: "Images",
    Links: "Useful Links",
    News: "News",
    ExpertSpeaks: "Expert Speaks",

    //Database
    BackupDb: "Backup Database",
    QueryWindow: "Query Window",
    DbMaintenance: "Database Maintenance",

    //Various
    GroupsMembers: "Group Members",
};

/**
 * AdminModuleRouteUrls is an object that stores route URLs for various admin modules in the application.
 * Each key is a member of the AdminModules enum, mapped to its corresponding URL string.
 */
export const AdminModuleRouteUrls = {
    [AdminModules.Dashboard]: `${AdminBaseUrl}/dashboard`,
    [AdminModules.MyAccount]: `${AdminBaseUrl}/myaccount`,
    [AdminModules.ParentCompany]: `${AdminBaseUrl}/parent-company`,
    [AdminModules.Company]: `${AdminBaseUrl}/company`,
    [AdminModules.Members]: `${AdminBaseUrl}/members`,
    [AdminModules.Groups]: `${AdminBaseUrl}/groups`,
    [AdminModules.ModuleAllocation]: `${AdminBaseUrl}/module-allocation`,
    [AdminModules.LoginBypassManager]: `${AdminBaseUrl}/login-bypass-manager`,
    [AdminModules.Country]: `${AdminBaseUrl}/country`,
    [AdminModules.State]: `${AdminBaseUrl}/state`,
    [AdminModules.City]: `${AdminBaseUrl}/city`,
    [AdminModules.Wm]: `${AdminBaseUrl}/manage-webmeeting`,
    [AdminModules.Meeting]: `${AdminBaseUrl}/manage-meeting`,
    [AdminModules.Forum]: `${AdminBaseUrl}/manage-forum`,
    [AdminModules.Survey]: `${AdminBaseUrl}/manage-survey`,
    [AdminModules.Pub]: `${AdminBaseUrl}/manage-publication`,
    [AdminModules.Articles]: `${AdminBaseUrl}/manage-articles`,
    [AdminModules.Doc]: `${AdminBaseUrl}/manage-document`,
    [AdminModules.CS]: `${AdminBaseUrl}/manage-casestudy`,
    [AdminModules.Videos]: `${AdminBaseUrl}/manage-videos`,
    [AdminModules.Images]: `${AdminBaseUrl}/manage-image`,
    [AdminModules.Links]: `${AdminBaseUrl}/manage-usefullinks`,
    [AdminModules.News]: `${AdminBaseUrl}/manage-news`,
    [AdminModules.ExpertSpeaks]: `${AdminBaseUrl}/expspeaks`,
    [AdminModules.Users]: `${AdminBaseUrl}/manage-users`,
    [AdminModules.UserRoles]: `${AdminBaseUrl}/manage-roles`,
    [AdminModules.Reports]: `${AdminBaseUrl}/manage-reports`,
    [AdminModules.BackupDb]: `${AdminBaseUrl}/backup-database`,
    [AdminModules.QueryWindow]: `${AdminBaseUrl}/query-window`,
    [AdminModules.DbMaintenance]: `${AdminBaseUrl}/manage-database`,
};

/**
 * AdminKeys is an object containing various constants used for managing admin-related session,
 * store, configuration, and tracking keys within the application.
 * This includes keys used for theme setting, session data handling, store management, navigation
 * configuration, and activity trackers.
 */
export const AdminKeys = {
    theme: 'theme',

    //session keys
    sessionData: '@dm!n$3$$!0nd@t@',
    lsSessionSuperKey: '@dm!n1$$3$$!0n$up3rk3y',
    lastUrlToRemember: '@dm!n!@$tur1t0r3m3mb3r',
    sessionToken: '@dm!ntk3n',
    loginExpired: 'loginExpired',

    //store-keys
    store_somePage_keyName: 'store_somePage_keyName__admin',

    //config
    config_navbarCurrPage: 'config_navbarCurrPage',

    //trackers
    tracker_SidebarToggleOn: 'tracker_SidebarToggleOn',
    tracker_currPageModule: 'tracker_currPageModule',
    tracker_currPageTag: 'tracker_currPageTag__admin',
};

/**
 * An object containing URL patterns for various admin-related routes.
 * Provides easy access to different pages in the administration section of the application.
 */
export const AdminGenRouteUrls = {
    login: `${AdminBaseUrl}/login`,
    requestAccess: `${AdminBaseUrl}/request_access`,
    forgotPw: `${AdminBaseUrl}/forgot-password`,
    dashboard: AdminModuleRouteUrls[AdminModules.Dashboard],
    _404: `${AdminBaseUrl}/*`,
    // _404: `/*`,

    //parent-company
    parentCompany_addPage: `${AdminBaseUrl}/add-pcompany`,
    parentCompany_editPage: `${AdminBaseUrl}/edit-pcompany?id=`,

    //company
    company_addPage: `${AdminBaseUrl}/add-company`,
    company_editPage: `${AdminBaseUrl}/edit-company?id=`,

    //member
    member_addPage: `${AdminBaseUrl}/add-member`,
    member_editPage: `${AdminBaseUrl}/edit-member?id=`,
    member_clonePage: `${AdminBaseUrl}/clone-member?id=`,

    //group
    groups_addPage: `${AdminBaseUrl}/add-group`,
    groups_editPage: `${AdminBaseUrl}/edit-group?id=`,
    groups_editGroupMembersPage: `${AdminBaseUrl}/edit-memgroup?id=`,

    //country
    country_addPage: `${AdminBaseUrl}/add-country`,
    country_editPage: `${AdminBaseUrl}/edit-country?id=`,

    //state
    state_addPage: `${AdminBaseUrl}/add-state`,
    state_editPage: `${AdminBaseUrl}/edit-state?id=`,

    //city
    city_addPage: `${AdminBaseUrl}/add-city`,
    city_editPage: `${AdminBaseUrl}/edit-city?id=`,

    //login_bypass_mngr
    login_bypass_mngr_editPage: `${AdminBaseUrl}/edit-bypasss-link?id=`,

    //expertSpeaks
    expertSpeaks_addPage: `${AdminBaseUrl}/add-expspeak`,
    expertSpeaks_editPage: `${AdminBaseUrl}/edit-expspeak?id=`,

    //userRoles
    userRoles_addPage: `${AdminBaseUrl}/add-role`,
    userRoles_editPage: `${AdminBaseUrl}/edit-roles?id=`,

    //users
    users_addPage: `${AdminBaseUrl}/add-user`,
    // users_editPage: `${AdminBaseUrl}/edit-user?id=`,

    //
    // _addPage: `${AdminBaseUrl}/`,
    // _editPage: `${AdminBaseUrl}/`,

};

/**
 * AdminReqUrls provides a comprehensive mapping of endpoint URLs for various administrative operations.
 * This object is categorized by different administrative functionalities such as authentication, parent component,
 * company, member, groups, country, state, city, various management (meeting, forum, survey, pub, article, doc, cs, img, vid, link),
 * experienced speakers, news, user roles, users, query window, and group members.
 */
export const AdminReqUrls = {
    //auth
    admin__login: '/admin-login',
    admin__logout: '/admin-logout',
    admin__forgotPw: '/admin-forgot-password',

    //dashboard
    admin__dashboard__fetchWidgetsData: '/admin-dashboard-fetch-widgets-data',

    //parent-comp
    admin__parentComp__mainPage_fetch_recordList: '/admin-parent-comp-main-page-fetch-record-list',
    admin__parentComp__deleteRecord: '/admin-parent-comp-delete-record',
    admin__parentComp_fetchEditPageRecord: '/admin-parent-comp-fetch-edit-page-record',
    admin__parentComp_addEditPage_submit_formData: '/admin-parent-comp-add-edit-page-submit-form-data',

    //company
    admin__company__mainPage_fetch_recordList: '/admin-company-main-page-fetch-record-list',
    admin__company__deleteRecord: '/admin-company-delete-record',
    admin__company_fetchEditPageRecord: '/admin-company-fetch-edit-page-record',
    admin__company_addEditPage_submit_formData: '/admin-company-add-edit-page-submit-form-data',

    //member
    admin__member__mainPage_fetch_recordList: '/admin-member-main-page-fetch-record-list',
    admin__member__mainPage_uploadExcelFile: '/admin-member-main-page-upload-excel-file',
    admin__member__deleteRecord: '/admin-member-delete-record',
    admin__member_fetchEditPageRecord: '/admin-member-fetch-edit-page-record',
    admin__member_addEditPage_submit_formData: '/admin-member-add-edit-page-submit-form-data',

    //groups
    admin__groups__mainPage_fetch_recordList: '/admin-groups-main-page-fetch-record-list',
    admin__groups__deleteRecord: '/admin-groups-delete-record',
    admin__groups_fetchEditPageRecord: '/admin-groups-fetch-edit-page-record',
    admin__groups_addEditPage_submit_formData: '/admin-groups-add-edit-page-submit-form-data',

    //country
    admin__country__mainPage_fetch_recordList: '/admin-country-main-page-fetch-record-list',
    admin__country__deleteRecord: '/admin-country-delete-record',
    admin__country_fetchEditPageRecord: '/admin-country-fetch-edit-page-record',
    admin__country_addEditPage_submit_formData: '/admin-country-add-edit-page-submit-form-data',

    //state
    admin__state__mainPage_fetch_recordList: '/admin-state-main-page-fetch-record-list',
    admin__state__deleteRecord: '/admin-state-delete-record',
    admin__state_fetchEditPageRecord: '/admin-tate-fetch-edit-page-record',
    admin__state_addEditPage_submit_formData: '/admin-state-add-edit-page-submit-form-data',

    //city
    admin__city__mainPage_fetch_recordList: '/admin-city-main-page-fetch-record-list',
    admin__city__deleteRecord: '/admin-city-delete-record',
    admin__city_fetchEditPageRecord: '/admin-city-fetch-edit-page-record',
    admin__city_addEditPage_submit_formData: '/admin-city-add-edit-page-submit-form-data',

    //module_alloc
    admin__moduleAlloc__mainPage_fetch_companies: '/admin-module-alloc-main-page-fetch-companies',
    admin__moduleAlloc__mainPage_fetch_curr_company_data: '/admin-module-alloc-main-page-fetch-curr-company-data',
    admin__moduleAlloc__mainPage_submitChanges: '/admin-module-alloc-main-page-submit-changes',

    //login_bypass
    admin__loginBypassMngr__mainPage_fetch_recordList: '/admin-login-bypass-mngr-main-page-fetch-record-list',
    admin__loginBypassMngr__edit__fetchEditRecord: '/admin-login-bypass-mngr-edit-fetch-edit-record',
    admin__loginBypassMngr__mainPage__deleteRecord: '/admin-login-bypass-mngr-main-page-delete-record',
    admin__loginBypassMngr__editPage__submitRecord: '/admin-login-bypass-mngr-main-page-update-record',

    //wm
    admin__wm__mainPage_fetch_recordList: '/admin-wm-main-page-fetch-record-list',
    admin__wm__deleteRecord: '/admin-wm-delete-record',

    //meeting
    admin__meeting__mainPage_fetch_recordList: '/admin-meeting-main-page-fetch-record-list',
    admin__meeting__deleteRecord: '/admin-meeting-delete-record',

    //forum
    admin__forum__mainPage_fetch_recordList: '/admin-forum-main-page-fetch-record-list',
    admin__forum__deleteRecord: '/admin-forum-delete-record',

    //survey
    admin__survey__mainPage_fetch_recordList: '/admin-survey-main-page-fetch-record-list',
    admin__survey__deleteRecord: '/admin-survey-delete-record',

    //pub
    admin__pub__mainPage_fetch_recordList: '/admin-pub-main-page-fetch-record-list',
    admin__pub__deleteRecord: '/admin-pub-delete-record',

    //article
    admin__article__mainPage_fetch_recordList: '/admin-article-main-page-fetch-record-list',
    admin__article__deleteRecord: '/admin-article-delete-record',

    //doc
    admin__doc__mainPage_fetch_recordList: '/admin-doc-main-page-fetch-record-list',
    admin__doc__deleteRecord: '/admin-doc-delete-record',

    //cs
    admin__cs__mainPage_fetch_recordList: '/admin-cs-main-page-fetch-record-list',
    admin__cs__deleteRecord: '/admin-cs-delete-record',

    //img
    admin__img__mainPage_fetch_recordList: '/admin-img-main-page-fetch-record-list',
    admin__img__deleteRecord: '/admin-img-delete-record',

    //vid
    admin__vid__mainPage_fetch_recordList: '/admin-vid-main-page-fetch-record-list',
    admin__vid__deleteRecord: '/admin-vid-delete-record',

    //link
    admin__link__mainPage_fetch_recordList: '/admin-link-main-page-fetch-record-list',
    admin__link__deleteRecord: '/admin-link-delete-record',

    //expSpk
    admin__expSpk__mainPage_fetch_recordList: '/admin-expSpk-main-page-fetch-record-list',
    admin__expSpk__deleteRecord: '/admin-expSpk-delete-record',
    admin__expSpk_fetchEditPageRecord: '/admin-expSpk-fetch-edit-page-record',
    admin__expSpk_addEditPage_submit_formData: '/admin-expSpk-add-edit-page-submit-form-data',

    //news
    admin__news__mainPage_fetch_recordList: '/admin-news-main-page-fetch-record-list',
    admin__news__deleteRecord: '/admin-news-delete-record',

    //userRoles
    admin__userRoles__mainPage_fetch_recordList: '/admin-userRoles-main-page-fetch-record-list',
    admin__userRoles__deleteRecord: '/admin-userRoles-delete-record',
    admin__userRoles_addEditPage_submit_formData: '/admin-userRoles-add-edit-page-submit-form-data',
    admin__userRoles_fetchEditPageRecord: '/admin-userRoles-fetch-edit-page-record',

    //users
    admin__users__mainPage_fetch_recordList: '/admin-users-main-page-fetch-record-list',
    admin__users__deleteRecord: '/admin-users-delete-record',
    // admin__users_fetchEditPageRecord: '/admin-users-fetch-edit-page-record',
    admin__users_addEditPage_submit_formData: '/admin-users-add-edit-page-submit-form-data',

    //query-window:
    admin__queryWindow_fetchRunQueryResults: '/admin-users-fetch-run-query-results',
    admin__queryWindow_fetchDbTables: '/admin-users-fetch-db-tables',

    //group-members
    admin__groupMembers_fetchEditPageRecord: '/admin-group-members-fetch-edit-page-record',
    admin__groupMembers_editPage_submit_formData: '/admin-group-members-edit-page-submit-form-data',

    //exp-report
    admin__expReport__usrRpt_fetchRecords: '/admin-exp-report-usr-rpt-fetch-records',
    admin__expReport__usagesRpt_fetchRecords: '/admin-exp-report-usages-rpt-fetch-records',
    admin__expReport__masterRpt_fetchRecords: '/admin-exp-report-master-rpt-fetch-records',

    //backup_db
    admin__backup_db__mainPage_fetch_curr_company_data: '/admin-backup-db-main-page-fetch-curr-company-data',
    admin__backup_db__mainPage_downloadCurrCompanyTablesData: '/admin-backup-db-main-page-download-curr-company-tables-data',

    //various
    fetchTimezones: `/fetch-timezones-data`,
    fetchCscData: `/fetch-csc-data`,
    fetchCountries: `/fetch-countries`,
    fetchStates: `/fetch-states`,
    fetchCities: `/fetch-cities`,
    fetchCompanyGroups: `/fetch-company-groups`,
};

/**
 Defines various types of cell contents for the admin module table.
 This constant object categorizes and identifies different types of data that can be displayed
 within cells of the admin module's table.
 */
export const AdminModuleTableCellType = {
    ID: "ID",
    EMAIL: "email",
    MOBILE: "mobile",
    NUMBER: "number",
    STRING: "string",
    LINK: "link",
    DATE: "date",
    DATE_SHORT: "date_short",
    YES_NO: "YES_NO", //YES/NO
    YES_NO_INT: "YES_NO_INT", //YES/NO if value>0
    YES_NO_TAG: "YES_NO_TAG", //YES/NO
    YES_NO_INT_TAG: "YES_NO_INT_TAG", //YES/NO if value>0
    YES_NO_TAG_REVERSE: "YES_NO_TAG_REVERSE", //YES/NO
    YES_NO_INT_TAG_REVERSE: "YES_NO_INT_TAG_REVERSE", //YES/NO if value>0
    STATUS_YN: "Status", //YES/NO status
    STATUS_EDIT_ENABLE_DISABLE_ACTIONS: "STATUS_EDIT_ENABLE_DISABLE_ACTIONS", //Status-Y/N, Enable/Disable toggle, Edit
    STATUS_ENABLE_DISABLE_ACTIONS: "STATUS_ENABLE_DISABLE_ACTIONS", //Status-Y/N, Enable/Disable toggle
    IMAGE: "IMAGE",
    IMAGE_CIRCULAR: "IMAGE_CIRCULAR",
    IMAGE_SQUARE: "IMAGE_SQUARE",
    CHECKBOX_COL: "CHECKBOX_COL",

    //parent-comp cell-type
    MEMBER_ACTION__EDIT_DELETE: "MEMBER_ACTION__EDIT_DELETE",
    GROUPS_MEMBS_ACTION__EDIT: "GROUPS_MEMBS_ACTION__EDIT",
    LOGIN_BPS_ACTION__EDIT: "LOGIN_BPS_ACTION__EDIT",
};

/**
 Represents the dataset containing field configurations for various Admin modules.
 The configuration is stored in key-value pairs where each key is an Admin module,
 and the value is an object containing metadata definitions for the fields in that module.
 Fields can include properties such as database column names, display titles, data types,
 visibility, sortability, searchability, exportability, and more.
 Admin modules covered include `MyAccount`, `ParentCompany`, `Company`, `Members`, `Groups`,
 `Country`, `State`, `City`, `Wm`, `Meeting`, `Forum`, `Survey`, `Pub`, `Articles`,
 `Doc`, `CS`, `Links`, `Videos`, `Images`, `ExpertSpeaks`, `News`, `UserRoles`, and `Users`.
 Field configurations facilitate data management, representation, and interactivity in the Admin interface.
 */
export const AdminModuleFieldsData = {
    [AdminModules.MyAccount]: {
        userId: {
            column: 'user_id',
            title: 'User Id'
        },
        userType: {
            column: 'user_type',
            title: 'User Type'
        },
        userName: {
            column: 'user_name',
            title: 'User Name'
        },
        password: {
            column: 'user_password',
            title: 'User Password'
        },
        passwordV2: {
            column: 'password_v2',
            title: 'Password V'
        },
        fullName: {
            column: 'fullname',
            title: 'Full Name'
        },
        email: {
            column: 'email',
            title: 'Email'
        },
        mobile: {
            column: 'mobileno',
            title: 'Mobile No.'
        },
        image: {
            column: 'image',
            title: 'Image'
        },
        remark: {
            column: 'remark',
            title: 'Remark'
        },
        assignCompany: {
            column: 'assign_company',
            title: 'Assign Company'
        },
        status: {
            column: 'status',
            title: 'Status | Action'
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        },
    },
    [AdminModules.ParentCompany]: {
        recordId: {
            column: 'pcompany_id',
            title: 'recordId',
            cellType: AdminModuleTableCellType.ID,
            show: false,
            defShow: false,
            sortable: false,
            hideable: true,
            searchable: false,
            exportable: false,
        },
        name: {
            column: 'pcompany_name',
            title: 'Parent Company',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'pcompany_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Added Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 160,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Company]: {
        recordId: {
            column: 'company_id',
            title: 'recordId'
        },
        parentCompanyId: {
            column: 'pcompany_id',
            title: 'Parent Company'
        },
        parentCompanyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__company__mainPage_fetch_recordList
            column: 'pcompany_name',
            title: 'Parent Company',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 120,
        },
        companyName: {
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        logo: {
            column: 'company_logo',
            title: 'Logo',
            titleAlt: 'Company Logo',
            cellType: AdminModuleTableCellType.IMAGE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 60,
        },
        contactPersonName1: {
            column: 'cperson_name1',
            title: 'Contact Person',
            titleAlt: 'Contact Person 1',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        phone: {
            column: 'company_phone',
            title: 'Mobile No.',
            cellType: AdminModuleTableCellType.MOBILE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 130,
        },
        email: {
            column: 'company_email',
            title: 'Email ID',
            cellType: AdminModuleTableCellType.EMAIL,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'company_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        brandLogo: {
            column: 'brand_logo',
            title: 'Brand Logo'
        },
        footerLogo: {
            column: 'footer_logo',
            title: 'Footer Logo'
        },
        logoExt: {
            column: 'logoExt',
            title: 'logoExt'
        },
        footerLogoExt: {
            column: 'footerLogoExt',
            title: 'footerLogoExt'
        },
        brandLogoExt: {
            column: 'brandLogoExt',
            title: 'brandLogoExt'
        },
        projectName: {
            column: 'project_name',
            title: 'Project Name'
        },
        countryId: {
            column: 'country_id',
            title: 'Country'
        },
        countryName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__company__mainPage_fetch_recordList
            column: 'country_name',
            title: 'Country Name'
        },
        stateId: {
            column: 'state_id',
            title: 'State'
        },
        stateName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__company__mainPage_fetch_recordList
            column: 'state_name',
            title: 'State Name'
        },
        cityId: {
            column: 'city_id',
            title: 'City'
        },
        cityName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__company__mainPage_fetch_recordList
            column: 'city_name',
            title: 'City Name'
        },
        address: {
            column: 'company_address',
            title: 'Address'
        },
        website: {
            column: 'company_website',
            title: 'Website'
        },
        timezoneName: {
            column: 'timezoneName',
            title: 'Timezone'
        },
        timezoneId: {
            column: 'timezone_id',
            title: 'Timezone'
        },
        remark: {
            column: 'company_remark',
            title: 'Remark'
        },
        getHelp: {
            column: 'get_help',
            title: 'Get Help'
        },
        contactPersonDesignation1: {
            column: 'cperson_designation1',
            title: 'Designation'
        },
        contactPersonPhone1: {
            column: 'cperson_phone1',
            title: 'Contact Phone No. 1'
        },
        contactPersonMobile1: {
            column: 'cperson_mobile1',
            title: 'Contact Mobile 1'
        },
        contactPersonEmail1: {
            column: 'cperson_email1',
            title: 'Contact Email 1',
        },
        contactPersonName2: {
            column: 'cperson_name2',
            title: 'Contact Person 2'
        },
        contactPersonDesignation2: {
            column: 'cperson_designation2',
            title: 'Designation'
        },
        contactPersonPhone2: {
            column: 'cperson_phone2',
            title: 'Contact Phone No. 2'
        },
        contactPersonMobile2: {
            column: 'cperson_mobile2',
            title: 'Contact Mobile 2'
        },
        contactPersonEmail2: {
            column: 'cperson_email2',
            title: 'Contact Email 2',
        },
        contactPersonName3: {
            column: 'cperson_name3',
            title: 'Contact Person 3'
        },
        contactPersonDesignation3: {
            column: 'cperson_designation3',
            title: 'Designation'
        },
        contactPersonPhone3: {
            column: 'cperson_phone3',
            title: 'Contact Phone No. 3'
        },
        contactPersonMobile3: {
            column: 'cperson_mobile3',
            title: 'Contact Mobile 3'
        },
        contactPersonEmail3: {
            column: 'cperson_email3',
            title: 'Contact Email 3',
        },
        managerName: {
            column: 'manager_name',
            title: 'Manager Name',
            titleAlt: 'Manager Name',
        },
        managerPhone: {
            column: 'manager_phone',
            title: 'Phone No.'
        },
        managerEmail: {
            column: 'manager_email',
            title: 'Manager Email ID',
            titleAlt: 'Email ID',
        },
        contentPeers: {
            column: 'content_peers',
            title: 'Content from Peers'
        },
        contentMedical: {
            column: 'content_medical',
            title: 'Content from Medical'
        },
        showCaseStudy: {
            column: 'case_study',
            title: 'Show Case Study'
        },
        showSurvey: {
            column: 'survey',
            title: 'Show Survey'
        },
        showExpertSpeak: {
            column: 'expert_speak',
            title: 'Show Expert Speak'
        },
        showImpUpdates: {
            column: 'imp_updates',
            title: 'Show Important Updates'
        },
        showArticles: {
            column: 'articles',
            title: 'Show Articles'
        },
        showUploadDocument: {
            column: 'upload_document',
            title: 'Show Upload Document'
        },
        showCreateForum: {
            column: 'create_forum',
            title: 'Show Create Forum'
        },
        forumValidate: {
            column: 'forum_validate',
            title: 'Forum Validate'
        },
        subdomain: {
            column: 'company_subdomain',
            title: 'Subdomain',
            titleAlt: 'Company Subdomain'
        },
        subdomainAccess: {
            column: 'company_subdomain_access',
            title: 'Subdomain Access'
        },
        welcomeImage: {
            column: 'welcome_image',
            title: 'Welcome Image'
        },
        welcomeImageExt: {
            column: 'welcomeImageExt',
            title: 'welcomeImageExt'
        },
        welcomeMsg: {
            column: 'welcome_msg',
            title: 'Welcome Message'
        },
        impUpdate: {
            column: 'imp_update',
            title: 'Important Update'
        },
        legalNotice: {
            column: 'legal_notice',
            title: 'Legal Notice'
        },
        termsOfUse: {
            column: 'privacy_policy',
            title: 'Terms of Use',
        },
        helpHeader: {
            column: 'help_header',
            title: 'Help Page Place Holder'
        },
        helpBottom: {
            column: 'help_bottom',
            title: 'Help Page Bottom Text'
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        },
        domainUrl: {
            column: 'domain_url',
            title: 'Domain Url'
        },
        domainPrefs: {
            column: 'domain_prefs',
            title: 'Domain Prefs'
        },
        loginImg: {
            column: 'login_img',
            title: 'Login Img'
        },
        reqacsImg: {
            column: 'reqacs_img',
            title: 'Reqacs Img'
        },
    },
    [AdminModules.Members]: {
        recordId: {
            column: "member_id",
            title: "member_id"
        },
        companyId: {
            column: "company_id",
            title: "Company"
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__member__mainPage_fetch_recordList
            column: "company_name",
            title: "Company Name",
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 140,
        },
        fullName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__member__mainPage_fetch_recordList
            column: "full_name",
            title: "Member Name",
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        memberImage: {
            column: "member_image",
            title: "Image",
            titleAlt: "Member Image",
            cellType: AdminModuleTableCellType.IMAGE_SQUARE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 80,
        },
        memberMobile1: {
            column: "member_mobile1",
            title: "Mobile No.",
            titleAlt: "Mobile No. 1",
            cellType: AdminModuleTableCellType.MOBILE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        memberEmail1: {
            column: "member_email1",
            title: "Email ID",
            titleAlt: "Email ID 1",
            cellType: AdminModuleTableCellType.EMAIL,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 160,
        },
        status: {
            column: "member_status",
            title: "Status",
            cellType: AdminModuleTableCellType.MEMBER_ACTION__EDIT_DELETE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 200,
        },
        memberPrefix: {
            column: "member_prefix",
            title: "Member Prefix"
        },
        firstName: {
            column: "first_name",
            title: "First Name"
        },
        lastName: {
            column: "last_name",
            title: "Last Name"
        },
        username: {
            column: "user_name",
            title: "Username"
        },
        userPassword: {
            column: "user_password",
            title: "User Password"
        },
        vokUserPassword: {
            column: "vok_user_password",
            title: "Vok User Password",
            titleAlt: "Password",
        },
        loginPin: {
            column: "login_pin",
            title: "Login PIN",
        },
        memberUser: {
            column: "member_user",
            title: "User Type",
        },
        memberDob: {
            column: "member_dob",
            title: "DOB"
        },
        gender: {
            column: "gender",
            title: "Gender"
        },
        memberEmail2: {
            column: "member_email2",
            title: "Email 2",
            titleAlt: "Email ID 2",
        },
        workPhone1: {
            column: "work_phone1",
            title: "Work Phone No. 1",
        },
        workPhone2: {
            column: "work_phone2",
            title: "Work Phone No. 2",
        },
        memberMobile2: {
            column: "member_mobile2",
            title: "Mobile No. 2",
        },
        homePhone: {
            column: "home_phone",
            title: "Home Phone No."
        },
        memberSpeciality1: {
            column: "member_speciality1",
            title: "Speciality 1",
        },
        memberSpeciality2: {
            column: "member_speciality2",
            title: "Speciality 2"
        },
        countryId: {
            column: "country_id",
            title: "Country"
        },
        stateId: {
            column: "state_id",
            title: "State Id"
        },
        cityId: {
            column: "city_id",
            title: "City Id"
        },
        countryName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__member__mainPage_fetch_recordList
            column: "country_name",
            title: "Country Name",
        },
        stateName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__member__mainPage_fetch_recordList
            column: "state_name",
            title: "State",
        },
        cityName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__member__mainPage_fetch_recordList
            column: "city_name",
            title: "City",
        },
        timezoneId: {
            column: "member_timezone",
            title: "Timezone"
        },
        timezoneName: {
            column: "member_timezone",
            title: "Timezone Name"
        },
        memberOrganisation1: {
            column: "member_organisation1",
            title: "Organisation 1"
        },
        memberDesignation1: {
            column: "member_designation1",
            title: "Designation 1"
        },
        memberOrganisation2: {
            column: "member_organisation2",
            title: "Organisation 2"
        },
        memberDesignation2: {
            column: "member_designation2",
            title: "Designation 2"
        },
        highestEducation: {
            column: "highest_education",
            title: "Highest Education"
        },
        interestArea1: {
            column: "interest_area1",
            title: "Interest Area 1"
        },
        interestArea2: {
            column: "interest_area2",
            title: "Interest Area 2"
        },
        memberBiography: {
            column: "member_biography",
            title: "Biography",
        },
        workAddress1: {
            column: "work_address1",
            title: "Work Address 1"
        },
        workAddress2: {
            column: "work_address2",
            title: "Work Address 2"
        },
        homeAddress: {
            column: "home_address",
            title: "Home Address"
        },
        memberRemark: {
            column: "member_remark",
            title: "Remark"
        },
        youHave: {
            column: "you_have",
            title: "You Have"
        },
        accessInternet: {
            column: "access_internet",
            title: "Access Internet"
        },
        sendReminder: {
            column: "send_reminder",
            title: "Send Reminder"
        },
        conTime: {
            column: "con_time",
            title: "Con Time"
        },
        otherTime: {
            column: "other_time",
            title: "Other Time"
        },
        conDay: {
            column: "con_day",
            title: "Con Day"
        },
        otherDay: {
            column: "other_day",
            title: "Other Day"
        },
        acceptPolicy: {
            column: "accept_policy",
            title: "Accept Policy"
        },
        insertDate: {
            column: "insert_date",
            title: "Insert Date"
        },
        insertBy: {
            column: "insert_by",
            title: "Insert By"
        },
        updateDate: {
            column: "update_date",
            title: "Update Date"
        },
        updateBy: {
            column: "update_by",
            title: "Update By"
        },
        timestamp: {
            column: "timestamp",
            title: "Timestamp"
        },
        memberOnline: {
            column: "member_online",
            title: "Online"
        },
        loginTime: {
            column: "login_time",
            title: "Login Time"
        },
        logoutTime: {
            column: "logout_time",
            title: "Logout Time"
        },
        totalLogin: {
            column: "total_login",
            title: "Total Login"
        },
        newTotalLogin: {
            column: "new_total_login",
            title: "New Total Login",
            field: "newTotalLogin"
        },
        memberImageExt: {
            column: "memberImageExt",
            title: "memberImageExt",
            field: "memberImageExt"
        },
        assignGroupId: {
            column: "assignGroupId",
            title: "Assign Group",
        },
        assignGroupName: {
            column: "assignGroupName",
            title: "Assign Group",
        },
    },
    [AdminModules.Groups]: {
        recordId: {
            column: 'group_id',
            title: 'Group Id'
        },
        companyId: {
            column: 'company_id',
            title: 'Company'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__groups__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company',
            titleAlt: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        name: {
            column: 'group_name',
            title: 'Group',
            titleAlt: 'Group Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'group_status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        members: {
            column: 'members',
            title: 'Members',
            cellType: AdminModuleTableCellType.GROUPS_MEMBS_ACTION__EDIT,
            show: true,
            defShow: true,
            sortable: false,
            hideable: true,
            searchable: false,
            exportable: false,
            width: 80,
        },
        memberIds: {
            column: 'member_id',
            title: 'Group Members'
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Country]: {
        recordId: {
            column: 'country_id',
            title: 'recordId'
        },
        name: {
            column: 'country_name',
            title: 'Country',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'country_status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.State]: {
        recordId: {
            column: 'state_id',
            title: 'recordId'
        },
        countryId: {
            column: 'country_id',
            title: 'Country'
        },
        countryName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__state__mainPage_fetch_recordList
            column: 'country_name',
            title: 'Country',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        stateName: {
            column: 'state_name',
            title: 'State',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'state_status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.City]: {
        recordId: {
            column: 'city_id',
            title: 'recordId'
        },
        countryId: {
            column: 'country_id',
            title: 'Country'
        },
        stateId: {
            column: 'state_id',
            title: 'State'
        },
        countryName: {
            column: 'country_name',
            title: 'Country',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        stateName: {
            column: 'state_name',
            title: 'State',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        cityName: {
            column: 'city_name',
            title: 'City',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'city_status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.LoginBypassManager]: {
        recordId: {
            column: 'id',
            title: 'Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        memberName: {
            column: 'member_name',
            title: 'Member Name',
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        companyName: {
            column: 'company_name',
            title: 'Company Name',
        },
        memberName_companyName: {
            column: 'memberName_companyName',
            title: 'Member Name | Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 260,
        },
        forAnyone: {
            column: 'for_anyone',
            title: 'Guest User',
            titleAlt: 'Guest User/For Anyone',
            cellType: AdminModuleTableCellType.YES_NO_INT_TAG,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        bypassUrl: {
            column: 'bypassUrl',
            title: 'Bypass Url',
            cellType: AdminModuleTableCellType.LINK,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 220,
        },
        redirectUrl: {
            column: 'redirect_url',
            title: 'Redirect Url',
            cellType: AdminModuleTableCellType.LINK,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 160,
        },
        shareLimit_usedCount: {
            column: 'shareLimit_usedCount',
            title: 'Share Limit | Used Count',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 160,
        },
        insertBy_expireAt: {
            column: 'insertBy_expireAt',
            title: 'Shared On | Expire Date',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 200,
        },
        isExpired: {
            column: 'isExpired',
            title: 'Expired',
            cellType: AdminModuleTableCellType.YES_NO_TAG_REVERSE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 140,
        },
        tokenAlias: {
            column: 'token_alias',
            title: 'Token Alias',
        },
        shareLimit: {
            column: 'share_limit',
            title: 'Share Limit',
        },
        usedCount: {
            column: 'used_count',
            title: 'Used Count',
        },
        expireAt: {
            column: 'expire_at',
            title: 'Expire Date',
        },
        status: {
            column: 'status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.LOGIN_BPS_ACTION__EDIT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 150,
        },
        expireAtJwt: {
            column: 'expire_at_jwt',
            title: 'Expire At Jwt'
        },
        token: {
            column: 'token',
            title: 'Token'
        },
        ipAddress: {
            column: 'ip_address',
            title: 'Ip Address'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By',
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Wm]: {
        recordId: {
            column: 'webmeeting_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__wm__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__wm__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        name: {
            column: 'webmeeting_title',
            title: 'Web Meeting Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        date: {
            column: 'webmeeting_date',
            title: 'Web Meeting Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 170,
        },
        status: {
            column: 'webmeeting_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Meeting]: {
        recordId: {
            column: 'meeting_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__wm__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__meeting__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        name: {
            column: 'meeting_title',
            title: 'Meeting Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        date: {
            column: 'meeting_date',
            title: 'Meeting Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 170,
        },
        status: {
            column: 'meeting_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Forum]: {
        recordId: {
            column: 'forum_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__wm__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__forum__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        name: {
            column: 'forum_topic',
            title: 'Forum Topic',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        date: {
            column: 'forum_startdate',
            title: 'Forum Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 170,
        },
        status: {
            column: 'forum_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Survey]: {
        recordId: {
            column: 'survey_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__wm__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__survey__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        name: {
            column: 'survey_topic',
            title: 'Survey Topic',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        date: {
            column: 'survey_startdate',
            title: 'Survey Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: false,
            width: 170,
        },
        status: {
            column: 'survey_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Pub]: {
        recordId: {
            column: 'publication_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__pub__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__pub__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'publication_title',
            title: 'Publication Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'publication_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'publication_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Articles]: {
        recordId: {
            column: 'article_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__article__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__article__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'article_title',
            title: 'Article Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'article_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'article_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Doc]: {
        recordId: {
            column: 'document_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__doc__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__doc__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'document_title',
            title: 'Document Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'document_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'document_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        url: {
            column: 'document_url',
            title: 'Document Url'
        },
        desc: {
            column: 'document_desc',
            title: 'Document Desc'
        },
        memberUser: {
            column: 'member_user',
            title: 'Member User'
        },
        author: {
            column: 'document_author',
            title: 'Document Author'
        },
        date: {
            column: 'publication_date',
            title: 'Document Date'
        },
        comeFrom: {
            column: 'come_from',
            title: 'Come From'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.CS]: {
        recordId: {
            column: 'casestudy_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__cs__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__cs__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'casestudy_title',
            title: 'Case Study Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'casestudy_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'casestudy_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Links]: {
        recordId: {
            column: 'usefullink_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__link__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__link__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'usefullink_title',
            title: 'Useful Link Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'usefullink_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'usefullink_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Videos]: {
        recordId: {
            column: 'video_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__vid__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__vid__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'video_title',
            title: 'Video Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'video_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'video_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Images]: {
        recordId: {
            column: 'image_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__img__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__img__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'image_title',
            title: 'Image',
            cellType: AdminModuleTableCellType.IMAGE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 220,
        },
        albumName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__img__mainPage_fetch_recordList
            column: 'album_name',
            title: 'Album Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 120,
        },
        recomBy: {
            column: 'image_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'image_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.ExpertSpeaks]: {
        recordId: {
            column: 'expspeak_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__expSpk__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        title: {
            column: 'expspeak_name',
            title: 'Expert Speak Name',
            titleAlt: 'Expert Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 320,
        },
        recomBy: {
            column: 'expspeak_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'expspeak_status',
            title: 'Status | Action',
            titleAlt: 'Status',
            // cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        description: {
            column: 'expspeak_desc',
            title: 'Description'
        },
        memberUser: {
            column: 'member_user',
            title: 'Member User'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.News]: {
        recordId: {
            column: 'news_id',
            title: 'recordId'
        },
        companyId: {
            column: 'company_id',
            title: 'Company Id'
        },
        memberId: {
            column: 'member_id',
            title: 'Member Id'
        },
        companyName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__news__mainPage_fetch_recordList
            column: 'company_name',
            title: 'Company Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 90,
        },
        memberName: {
            //Depends on QUERY-JOIN at: backend/adminDbHandler.admin__news__mainPage_fetch_recordList
            column: 'member_name',
            title: 'Member',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        title: {
            column: 'news_title',
            title: 'News Title',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        insertBy: {
            column: 'insert_by',
            title: 'Created By',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 140,
        },
        recomBy: {
            column: 'news_recomby',
            title: 'Recommended',
            cellType: AdminModuleTableCellType.YES_NO_INT,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 100,
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date',
            cellType: AdminModuleTableCellType.DATE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 170,
        },
        status: {
            column: 'news_status',
            title: 'Status | Action',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.UserRoles]: {
        recordId: {
            column: 'roles_id',
            title: 'recordId'
        },
        name: {
            column: 'roles_name',
            title: 'Role Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        permission: {
            column: 'permission',
            title: 'Permission',
            titleAlt: 'Access Permissions',
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
    [AdminModules.Users]: {
        recordId: {
            column: 'user_id',
            title: 'User Id'
        },
        type: {
            column: 'user_type',
            // title: 'User Type',
            title: 'User Id',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 50,
        },
        username: {
            column: 'user_name',
            title: 'User Name',
            titleAlt: 'Username',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 160,
        },
        fullName: {
            column: 'fullname',
            title: 'Full Name',
            cellType: AdminModuleTableCellType.STRING,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 160,
        },
        email: {
            column: 'email',
            title: 'Email ID',
            cellType: AdminModuleTableCellType.EMAIL,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 220,
        },
        mobile: {
            column: 'mobileno',
            title: 'Mobile No.',
            cellType: AdminModuleTableCellType.MOBILE,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 180,
        },
        status: {
            column: 'status',
            title: 'Status | Action',
            titleAlt: 'Status',
            cellType: AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS,
            show: true,
            defShow: true,
            sortable: true,
            hideable: true,
            searchable: true,
            exportable: true,
            width: 110,
        },
        password: {
            column: 'user_password',
            title: 'User Password'
        },
        passwordV2: {
            column: 'password_v2',
            title: 'Password V2',
            titleAlt: 'Password',
        },
        confirmPassword: {
            column: 'confirmPassword',
            title: 'Confirm Password',
        },
        insertDate: {
            column: 'insert_date',
            title: 'Insert Date'
        },
        insertBy: {
            column: 'insert_by',
            title: 'Insert By'
        },
        updateDate: {
            column: 'update_date',
            title: 'Update Date'
        },
        remark: {
            column: 'remark',
            title: 'Remark'
        },
        assignCompany: {
            column: 'assign_company',
            title: 'Assign Company',
        },
        roleId: {
            column: 'user_type',
            title: 'User Role'
        },
        roleName: {
            column: 'roleName',
            title: 'User Role'
        },
        updateBy: {
            column: 'update_by',
            title: 'Update By'
        }
    },
};
//--------------ADMIN: Backend-Frontend common fields end-------------------------------

/**
 AdminModuleFormReqUrls defines URL mappings for administrative module forms.
 Each key represents an administrative module, used for operations like `delete`,
 `edit`, `add`, or other specific actions.
 */
export const AdminModuleFormReqUrls = {
    [AdminModules.ParentCompany]: {
        delete: AdminReqUrls.admin__parentComp__deleteRecord,
        edit: AdminGenRouteUrls.parentCompany_editPage,
        add: AdminGenRouteUrls.parentCompany_addPage,
    },
    [AdminModules.Company]: {
        delete: AdminReqUrls.admin__company__deleteRecord,
        edit: AdminGenRouteUrls.company_editPage,
        add: AdminGenRouteUrls.company_addPage,
    },
    [AdminModules.Members]: {
        delete: AdminReqUrls.admin__member__deleteRecord,
        edit: AdminGenRouteUrls.member_editPage,
        clone: AdminGenRouteUrls.member_clonePage,
        add: AdminGenRouteUrls.member_addPage,
    },
    [AdminModules.Groups]: {
        delete: AdminReqUrls.admin__groups__deleteRecord,
        edit: AdminGenRouteUrls.groups_editPage,
        editGroupMembers: AdminGenRouteUrls.groups_editGroupMembersPage,
        add: AdminGenRouteUrls.groups_addPage,
    },
    [AdminModules.Country]: {
        delete: AdminReqUrls.admin__country__deleteRecord,
        edit: AdminGenRouteUrls.country_editPage,
        add: AdminGenRouteUrls.country_addPage,
    },
    [AdminModules.State]: {
        delete: AdminReqUrls.admin__state__deleteRecord,
        edit: AdminGenRouteUrls.state_editPage,
        add: AdminGenRouteUrls.state_addPage,
    },
    [AdminModules.City]: {
        delete: AdminReqUrls.admin__city__deleteRecord,
        edit: AdminGenRouteUrls.city_editPage,
        add: AdminGenRouteUrls.city_addPage,
    },
    [AdminModules.LoginBypassManager]: {
        delete: AdminReqUrls.admin__loginBypassMngr__mainPage__deleteRecord,
        edit: AdminGenRouteUrls.login_bypass_mngr_editPage,
    },
    [AdminModules.Wm]: {
        delete: AdminReqUrls.admin__wm__deleteRecord,
    },
    [AdminModules.Meeting]: {
        delete: AdminReqUrls.admin__meeting__deleteRecord,
    },
    [AdminModules.Forum]: {
        delete: AdminReqUrls.admin__forum__deleteRecord,
    },
    [AdminModules.Survey]: {
        delete: AdminReqUrls.admin__survey__deleteRecord,
    },

    [AdminModules.Pub]: {
        delete: AdminReqUrls.admin__pub__deleteRecord,
    },
    [AdminModules.Articles]: {
        delete: AdminReqUrls.admin__article__deleteRecord,
    },
    [AdminModules.Doc]: {
        delete: AdminReqUrls.admin__doc__deleteRecord,
    },
    [AdminModules.CS]: {
        delete: AdminReqUrls.admin__cs__deleteRecord,
    },
    [AdminModules.Links]: {
        delete: AdminReqUrls.admin__link__deleteRecord,
    },
    [AdminModules.Videos]: {
        delete: AdminReqUrls.admin__vid__deleteRecord,
    },
    [AdminModules.Images]: {
        delete: AdminReqUrls.admin__img__deleteRecord,
    },
    [AdminModules.ExpertSpeaks]: {
        delete: AdminReqUrls.admin__expSpk__deleteRecord,
        add: AdminGenRouteUrls.expertSpeaks_addPage,
        edit: AdminGenRouteUrls.expertSpeaks_editPage,
    },
    [AdminModules.News]: {
        delete: AdminReqUrls.admin__news__deleteRecord,
    },

    [AdminModules.UserRoles]: {
        delete: AdminReqUrls.admin__userRoles__deleteRecord,
        edit: AdminGenRouteUrls.userRoles_editPage,
        add: AdminGenRouteUrls.userRoles_addPage,
    },
    [AdminModules.Users]: {
        delete: AdminReqUrls.admin__users__deleteRecord,
        // edit: AdminGenRouteUrls.users_editPage,
        add: AdminGenRouteUrls.users_addPage,
    },
};

export const ConfigConstants = {
    gridSpacing: 3,
    drawerWidth: 260,
};

/**
 * Enum-like object that defines various types of views for displaying content.
 * Used to specify how content should be presented in the application.
 */

export const ShowViewType = {
    PANEL: 'PANEL',
    DIALOG: 'DIALOG',
    DRAWER: 'DRAWER',
    PAGE: 'PAGE',
};
